import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import { getDealsForSelectedArea } from '../services/apiService';
import WalkerBottomNav from '../components/WalkerBottomNav';
import Logo from '../components/Logo';
import './MapPage.css';

function addJitter(lat, lng, index, total) {
  if (total <= 1) return { lat, lng };
  const radius = 0.0002; // ~22m
  const angle = (index / total) * 2 * Math.PI;
  return { lat: lat + radius * Math.cos(angle), lng: lng + radius * Math.sin(angle) };
}

const AREA_META = {
  'Amsterdam Zuid AS':               { lat: 52.3367, lng: 4.8688 },
  'Winkelcentrum Amsterdamse Poort': { lat: 52.3139, lng: 4.9525 },
  'Alexandrium Rotterdam':           { lat: 51.9490, lng: 4.5582 },
  'Citymall Almere':                 { lat: 52.3703, lng: 5.2141 },
  'Winkelcentrum Gelderlandplein':   { lat: 52.3389, lng: 4.8774 },
  'Stadshart Lelystad':              { lat: 52.5088, lng: 5.4721 },
  'Shopping Center Hoge Schie':      { lat: 51.9225, lng: 4.4792 },
};

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

export default function MapPage() {
  const navigate = useNavigate();
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const userMarkerRef = useRef(null);
  const [dealsWithLocation, setDealsWithLocation] = useState([]);
  const [dealsWithoutLocation, setDealsWithoutLocation] = useState([]);

  const areaName =
    sessionStorage.getItem('walkin_area_name') ||
    sessionStorage.getItem('walkin_shop_area') ||
    'Winkelcentrum Gelderlandplein';
  const userLat = parseFloat(sessionStorage.getItem('walkin_user_lat'));
  const userLng = parseFloat(sessionStorage.getItem('walkin_user_lng'));

  useEffect(() => {
    if (mapRef.current) return;

    // Start where the walker is (set by /detect, also for a manually picked
    // gebied). The hardcoded list only knows the original seven gebieden, so
    // newer ones such as Tricht Geldermalsen opened the map in Amsterdam.
    const meta = (!isNaN(userLat) && !isNaN(userLng))
      ? { lat: userLat, lng: userLng }
      : (AREA_META[areaName] || AREA_META['Winkelcentrum Gelderlandplein']);

    const map = L.map(mapDivRef.current, {
      center: [meta.lat, meta.lng],
      zoom: 15,
      zoomControl: true,
    });
    mapRef.current = map;

    // Dark basemap — the default OSM tiles are bright and fight the navy
    // chrome. CARTO's dark_all style is free for this kind of use provided
    // both CARTO and OpenStreetMap are credited.
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      attribution:
        '© <a href="https://openstreetmap.org/copyright">OpenStreetMap</a> · ' +
        '© <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 19,
    }).addTo(map);

    // ── deals + markers, loaded from the backend ──────────────────────────
    const addDealMarkers = (withLocation) => {
    // Group overlapping deals so we can jitter them out of the same pixel
    const locationGroups = {};
    withLocation.forEach((deal) => {
      const key = `${deal.shopLat.toFixed(4)},${deal.shopLng.toFixed(4)}`;
      if (!locationGroups[key]) locationGroups[key] = [];
      locationGroups[key].push(deal);
    });

    const clusterGroup = L.markerClusterGroup({
      maxClusterRadius: 40,
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        return L.divIcon({
          html: `<div class="map-marker-cluster">${count}</div>`,
          className: '',
          iconSize: [38, 38],
          iconAnchor: [19, 19],
        });
      },
    });

    Object.values(locationGroups).forEach((deals) => {
      deals.forEach((deal, index) => {
        const { lat, lng } = addJitter(deal.shopLat, deal.shopLng, index, deals.length);

        const icon = L.divIcon({
          className: '',
          html: '<div class="map-marker-deal"></div>',
          iconSize: [18, 18],
          iconAnchor: [9, 9],
          popupAnchor: [0, -10],
        });

        const validUntil = deal.expiresAt
          ? new Date(deal.expiresAt).toLocaleTimeString('nl-NL', {
              hour: '2-digit', minute: '2-digit',
            })
          : '23:59';
        const instructionsHtml = deal.shopInstructions
          ? `<div class="map-popup__instructions">${escapeHtml(deal.shopInstructions)}</div>`
          : '';
        const navHref =
          /iPad|iPhone|iPod/.test(navigator.userAgent)
            ? `maps://maps.apple.com/?daddr=${deal.shopLat},${deal.shopLng}&q=${encodeURIComponent(deal.shopName)}`
            : `https://www.google.com/maps/dir/?api=1&destination=${deal.shopLat},${deal.shopLng}`;
        const popupHtml = `
          <div class="map-popup">
            <div class="map-popup__shop">${escapeHtml(deal.shopName || '')}</div>
            <div class="map-popup__deal">${escapeHtml(deal.title || '')}</div>
            ${instructionsHtml}
            <div class="map-popup__time">Tot ${escapeHtml(validUntil)}</div>
            <div class="map-popup__buttons">
              <a class="map-popup__link" href="/deal/${escapeHtml(deal.id)}">Bekijk deal →</a>
              <a class="map-popup__nav" href="${navHref}">Route</a>
            </div>
          </div>`;
        const marker = L.marker([lat, lng], { icon }).bindPopup(popupHtml);
        clusterGroup.addLayer(marker);
      });
    });

    map.addLayer(clusterGroup);

    // Fit map to all markers if there's anything to fit to
    if (withLocation.length > 0) {
      const bounds = L.latLngBounds(withLocation.map((d) => [d.shopLat, d.shopLng]));
      if (!isNaN(userLat) && !isNaN(userLng)) bounds.extend([userLat, userLng]);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 17 });
    }
    };

    let cancelled = false;
    (async () => {
      let deals = [];
      // Same gebied scoping as the feed. The walker's own position is already
      // read as userLat/userLng above and is only used for the map view.
      try {
        const res = await getDealsForSelectedArea();
        deals = Array.isArray(res) ? res : [];
      } catch { deals = []; }
      if (cancelled) return;

      // Only deals whose retailer has real coordinates can be pinned; the
      // rest are listed under the map.
      const now = new Date();
      const withLocation = [];
      const withoutLocation = [];
      deals
        .filter((d) => d.status === 'actief' && d.expiresAt && new Date(d.expiresAt) > now)
        .forEach((deal) => {
          const dlat = deal.lat != null ? parseFloat(deal.lat) : NaN;
          const dlng = deal.lng != null ? parseFloat(deal.lng) : NaN;
          if (!isNaN(dlat) && !isNaN(dlng)) {
            withLocation.push({
              ...deal,
              shopLat: dlat,
              shopLng: dlng,
              shopInstructions: deal.instructions || null,
            });
          } else {
            withoutLocation.push(deal);
          }
        });

      setDealsWithLocation(withLocation);
      setDealsWithoutLocation(withoutLocation);
      addDealMarkers(withLocation);
    })();

    // User location marker (blue dot)
    const hasUserPos = !isNaN(userLat) && !isNaN(userLng);
    const ulat = hasUserPos ? userLat : meta.lat + 0.0008;
    const ulng = hasUserPos ? userLng : meta.lng + 0.0008;
    const userIcon = L.divIcon({
      className: '',
      html: '<div class="map-marker-user"><div class="map-marker-user__ring"></div></div>',
      iconSize: [24, 24],
      iconAnchor: [12, 12],
    });
    userMarkerRef.current = L.marker([ulat, ulng], { icon: userIcon })
      .addTo(map)
      .bindPopup('Je bent hier');

    map.on('popupopen', (e) => {
      const link = e.popup.getElement()?.querySelector('.map-popup__link');
      if (link) {
        link.addEventListener('click', (ev) => {
          ev.preventDefault();
          navigate(link.getAttribute('href'));
        });
      }
    });

    return () => {
      cancelled = true;
      map.remove();
      mapRef.current = null;
    };
  }, [areaName, userLat, userLng, navigate]);

  const centerOnMe = () => {
    const map = mapRef.current;
    if (!map) return;
    if (!isNaN(userLat) && !isNaN(userLng)) {
      map.flyTo([userLat, userLng], 16);
      return;
    }
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => {
        map.flyTo([p.coords.latitude, p.coords.longitude], 16);
        if (userMarkerRef.current) {
          userMarkerRef.current.setLatLng([p.coords.latitude, p.coords.longitude]);
        }
        sessionStorage.setItem('walkin_user_lat', String(p.coords.latitude));
        sessionStorage.setItem('walkin_user_lng', String(p.coords.longitude));
      },
      () => {},
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  return (
    <div className="map-page">
      <header
        className="map-page__header"
        style={{ paddingTop: 'max(env(safe-area-inset-top), 14px)' }}
      >
        <Logo size={20} className="map-page__logo" />
        <div className="map-page__area-chip">{areaName}</div>
        <button className="map-page__bell" aria-label="Notificaties">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </button>
      </header>

      <div className="map-page__map" ref={mapDivRef} />

      <div className="map-page__legend">
        <div className="map-page__legend-item">
          <div className="map-page__legend-dot map-page__legend-dot--deal" />
          <span>{dealsWithLocation.length} winkels met exacte locatie</span>
        </div>
        {dealsWithoutLocation.length > 0 && (
          <div className="map-page__legend-item">
            <div className="map-page__legend-dot map-page__legend-dot--gray" />
            <span>{dealsWithoutLocation.length} winkels zonder locatie</span>
          </div>
        )}
        <div className="map-page__legend-item">
          <div className="map-page__legend-dot map-page__legend-dot--blue" />
          <span>Jouw locatie</span>
        </div>
      </div>

      {dealsWithoutLocation.length > 0 && (
        <div className="map-page__no-location">
          <p>{dealsWithoutLocation.length} winkels hebben nog geen GPS locatie:</p>
          {dealsWithoutLocation.map((d) => (
            <div
              key={d.id}
              className="map-page__no-location-item"
              onClick={() => navigate(`/deal/${d.id}`)}
            >
              {d.shopName} — {d.title}
            </div>
          ))}
        </div>
      )}

      <button
        className="map-page__center-btn"
        onClick={centerOnMe}
        aria-label="Centreer op mij"
      >
        Centreer op mij
      </button>

      <WalkerBottomNav />
    </div>
  );
}
